// 消息项组件

import React, { useRef, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { MessageItemProps } from '../types';
import { MessageUtils } from '../utils/message';
import LazyImage from './LazyImage';

// 优化的消息组件
export const MessageItem = React.memo<MessageItemProps>(({ message, onRetry, onMarkAsRead }) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsRead = useRef(false); // 防止重复标记

  const formattedTime = useMemo(() =>
    MessageUtils.formatMessageTime(message.timestamp),
    [message.timestamp]
  );

  const contentLines = useMemo(() =>
    MessageUtils.splitMessageContent(message.content),
    [message.content]
  );

  // 检测媒体内容
  const mediaContent = useMemo(() =>
    MessageUtils.detectMediaContent(message.content),
    [message.content]
  );

  // 提取纯文本内容
  const textContent = useMemo(() =>
    MessageUtils.extractTextContent(message.content),
    [message.content]
  );

  // 视口检测，标记消息为已读
  useEffect(() => {
    // 重置标记状态（当 messageKey 或 readFlag 变化时）
    if (message.readFlag !== 0) {
      hasMarkedAsRead.current = false;
    }
  }, [message.messageKey, message.readFlag]);

  useEffect(() => {
    if (message.type === 'bot' && message.messageKey && message.readFlag === 0 && onMarkAsRead && !hasMarkedAsRead.current) {
      let observer: IntersectionObserver | null = null;
      
      try {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // 只在未标记且消息可见时触发
              if (entry.isIntersecting && !hasMarkedAsRead.current) {
                hasMarkedAsRead.current = true; // 立即标记，防止重复调用
                onMarkAsRead(message.messageKey!);
                if (observer) {
                  observer.unobserve(entry.target);
                }
              }
            });
          },
          { threshold: 0.5 } // 当消息50%可见时标记为已读
        );

        if (messageRef.current) {
          observer.observe(messageRef.current);
        }
      } catch (error) {
        console.warn('IntersectionObserver not supported:', error);
      }

      return () => {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      };
    }
  }, [message.type, message.messageKey, message.readFlag]);

  return (
    <div
      ref={messageRef}
      className={`chat-widget__message chat-widget__message--${message.type}`}
    >
      <div className="chat-widget__message-content">
        {/* 文本内容 */}
        {textContent && (
          <div className="chat-widget__message-text">
            {MessageUtils.splitMessageContent(textContent).map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        )}
        
        {/* 图片内容 - 懒加载 */}
        {mediaContent.hasImages && (
          <div className="chat-widget__message-images">
            {mediaContent.imageUrls.map((imageUrl, index) => (
              <LazyImage
                key={index}
                src={imageUrl}
                alt={`消息图片 ${index + 1}`}
                className="chat-widget__message-image"
                onLoad={() => console.log(`图片 ${index + 1} 加载完成`)}
                onError={() => console.warn(`图片 ${index + 1} 加载失败`)}
              />
            ))}
          </div>
        )}
        
        {/* 视频内容 */}
        {mediaContent.hasVideos && (
          <div className="chat-widget__message-videos">
            {mediaContent.videoUrls.map((videoUrl, index) => (
              <video
                key={index}
                className="chat-widget__message-video"
                controls
                preload="metadata"
                style={{ maxWidth: '100%', height: 'auto' }}
              >
                <source src={videoUrl} />
                您的浏览器不支持视频播放
              </video>
            ))}
          </div>
        )}
        
        {/* 链接内容 */}
        {mediaContent.hasLinks && (
          <div className="chat-widget__message-links">
            {mediaContent.linkUrls.map((linkUrl, index) => (
              <a
                key={index}
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="chat-widget__message-link"
              >
                <Icon icon="ph:link" />
                {linkUrl}
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="chat-widget__message-footer">
        <div className="chat-widget__message-time">
          {formattedTime}
        </div>
        {message.type === 'user' && message.status && (
          <div className="chat-widget__message-status">
            {message.status === 'sending' && (
              <div className="chat-widget__loading-spinner">
                <div className="chat-widget__spinner"></div>
              </div>
            )}
            {message.status === 'failed' && (
              <button
                className="chat-widget__retry-btn"
                onClick={() => onRetry(message.id)}
              >
                <Icon icon="ph:arrow-clockwise" />
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

MessageItem.displayName = 'MessageItem';